import { useMutation, useQuery } from '@tanstack/react-query';

import api from '@/hooks/useApi';

// ============================================================================
// Types
// ============================================================================

export type PackId = 'small' | 'medium';
export type SubscriptionTierId = 'narrator' | 'storyteller' | 'trailblazer';

export interface CreateCheckoutRequest {
  productType: 'pack' | 'subscription';
  productId: PackId | SubscriptionTierId;
  successUrl: string;
  cancelUrl: string;
}

export interface CreateCheckoutResponse {
  checkoutUrl: string;
  sessionId: string;
}

export interface SubscriptionStatus {
  hasSubscription: boolean;
  tier?: SubscriptionTierId;
  status?: 'active' | 'past_due' | 'canceled';
  currentPeriodEnd?: string;
  tokensRemaining?: number;
  stripeCustomerId?: string;
  cancelAtPeriodEnd?: boolean;
  pendingTier?: SubscriptionTierId;
  platform?: 'stripe' | 'apple';
  managementHint?: string;
}

export interface CreatePortalSessionResponse {
  portalUrl: string;
}

// ============================================================================
// Product Definitions (for UI display)
// ============================================================================

export const TOKEN_PACKS = [
  {
    id: 'small' as PackId,
    name: 'Small Pack',
    tokens: 25,
    price: '$4.99',
    priceCents: 499,
  },
  {
    id: 'medium' as PackId,
    name: 'Medium Pack',
    tokens: 60,
    price: '$9.99',
    priceCents: 999,
  },
] as const;

export const SUBSCRIPTION_TIERS = [
  {
    id: 'narrator' as SubscriptionTierId,
    name: 'Narrator',
    tokensPerMonth: 30,
    price: '$3.99/mo',
    priceCents: 399,
    description: 'Perfect for casual readers',
  },
  {
    id: 'storyteller' as SubscriptionTierId,
    name: 'Storyteller',
    tokensPerMonth: 60,
    price: '$7.99/mo',
    priceCents: 799,
    description: 'Great for regular adventurers',
    popular: true,
  },
  {
    id: 'trailblazer' as SubscriptionTierId,
    name: 'Trailblazer',
    tokensPerMonth: 120,
    price: '$14.99/mo',
    priceCents: 1499,
    description: 'For dedicated explorers',
  },
] as const;

// ============================================================================
// Query Keys
// ============================================================================

export const checkoutKeys = {
  all: ['checkout'] as const,
  subscription: () => [...checkoutKeys.all, 'subscription'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Create a Stripe checkout session and redirect
 */
export function useCreateCheckout() {
  return useMutation({
    mutationFn: async (request: CreateCheckoutRequest) => {
      const response = await api.post<CreateCheckoutResponse>('/payments/checkout', request);
      return response.data;
    },
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      window.location.href = data.checkoutUrl;
    },
  });
}

/**
 * Get current subscription status
 */
export function useSubscriptionStatus() {
  return useQuery({
    queryKey: checkoutKeys.subscription(),
    queryFn: async () => {
      const response = await api.get<SubscriptionStatus>('/payments/subscription');
      return response.data;
    },
  });
}

/**
 * Helper to get the base URL for success/cancel redirects
 */
export function getCheckoutUrls() {
  const baseUrl = window.location.origin;
  return {
    successUrl: `${baseUrl}/checkout/success`,
    cancelUrl: `${baseUrl}/checkout/cancel`,
  };
}

/**
 * Create a Stripe billing portal session and redirect
 */
export function useCreatePortalSession() {
  return useMutation({
    mutationFn: async () => {
      // Use dedicated portal return page to invalidate cached data after changes
      const returnUrl = `${window.location.origin}/portal/return`;
      const response = await api.post<CreatePortalSessionResponse>('/payments/portal', {
        returnUrl,
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Redirect to Stripe billing portal
      window.location.href = data.portalUrl;
    },
  });
}
